import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Search, Flag, Globe, Landmark, Scale,
  BookOpen, FileText, Map, CheckSquare, Folder, Cpu, ClipboardCheck,
  Calendar, Link2, Notebook, ShieldCheck,
  Settings, User, LogOut, Menu, X, ChevronRight, Sparkles, BarChart3
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { ATLAS_LOGO_URL, ATLAS_NAME, ATLAS_SUBTITLE } from '@/lib/brand';

// Sidebar IA — from /app/design_guidelines.json
// Each item routes to an existing functional page (no broken links).
const NAV_GROUPS = [
  {
    label: 'CORE',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, path: '/dashboard' },
      { label: 'Search & Browse', icon: Search, path: '/search' },
    ],
  },
  {
    label: 'CURRICULUM',
    items: [
      { label: 'US History', icon: Flag, path: '/curriculum/us-history' },
      { label: 'World History', icon: Globe, path: '/curriculum/world-history' },
      { label: 'Civics', icon: Landmark, path: '/curriculum/civics' },
      { label: 'Government', icon: Scale, path: '/curriculum/government' },
    ],
  },
  {
    label: 'RESOURCES',
    items: [
      { label: 'Lesson Plans', icon: BookOpen, path: '/lecture-index' },
      { label: 'Primary Sources', icon: FileText, path: '/primary-sources-index' },
      { label: 'Interactive Maps', icon: Map, path: '/maps' },
      { label: 'Standards', icon: CheckSquare, path: '/standards' },
      { label: 'State Assessments', icon: ClipboardCheck, path: '/state-assessments' },
      { label: 'Daily Questions', icon: Calendar, path: '/daily-questions' },
      { label: 'Teacher Toolkit', icon: Link2, path: '/teacher-toolkit' },
    ],
  },
  {
    label: 'WORKSPACE',
    items: [
      { label: 'My Library', icon: Notebook, path: '/library' },
      { label: 'Collections', icon: Folder, path: '/collections' },
      { label: 'AI Tools', icon: Cpu, path: '/ai-generate' },
    ],
  },
];

const SidebarLink = ({ item, active, onClick }) => {
  const Icon = item.icon;
  return (
    <button
      onClick={onClick}
      data-testid={`sidebar-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`group w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
        active
          ? 'bg-[#1D2D50] text-amber-50 shadow-inner'
          : 'text-[#FDFBF7]/70 hover:text-[#FDFBF7] hover:bg-white/5'
      }`}
    >
      <Icon className="h-4 w-4 flex-shrink-0" strokeWidth={1.75} />
      <span className="font-medium tracking-tight">{item.label}</span>
      {active && <ChevronRight className="h-3 w-3 ml-auto opacity-60" />}
    </button>
  );
};

const Sidebar = ({ onNavigate, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();

  const go = (path) => {
    navigate(path);
    if (onNavigate) onNavigate();
  };

  return (
    <aside
      className="flex flex-col w-64 h-screen bg-[#14213D] text-[#FDFBF7] px-5 py-6 border-r border-black/20"
      data-testid="app-sidebar"
    >
      {/* Brand */}
      <div className="flex items-center justify-between mb-8 px-2">
        <button
          onClick={() => go('/dashboard')}
          className="flex items-center gap-2.5 group min-w-0"
          data-testid="sidebar-brand"
        >
          <div className="w-10 h-10 rounded-md bg-[#FDFBF7] flex items-center justify-center flex-shrink-0 overflow-hidden border border-white/10">
            <img src={ATLAS_LOGO_URL} alt="" className="w-full h-full object-contain" />
          </div>
          <div className="flex flex-col leading-tight text-left min-w-0">
            <span className="font-serif-display text-lg tracking-tight truncate">{ATLAS_NAME}</span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-[#FDFBF7]/40 truncate">{ATLAS_SUBTITLE}</span>
          </div>
        </button>
        {onClose && (
          <button onClick={onClose} className="md:hidden text-[#FDFBF7]/60 hover:text-[#FDFBF7]" aria-label="Close menu">
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Nav groups */}
      <nav className="flex-1 overflow-y-auto -mr-2 pr-2 space-y-6">
        {NAV_GROUPS.map((group) => (
          <div key={group.label}>
            <p className="text-[11px] font-semibold tracking-[0.18em] text-[#FDFBF7]/40 mb-2 px-3">{group.label}</p>
            <div className="space-y-0.5">
              {group.items.map((item) => (
                <SidebarLink
                  key={item.path}
                  item={item}
                  active={location.pathname === item.path || (item.path === '/dashboard' && location.pathname.startsWith('/dashboard'))}
                  onClick={() => go(item.path)}
                />
              ))}
            </div>
          </div>
        ))}

        {isAdmin && (
          <div>
            <p className="text-[11px] font-semibold tracking-[0.18em] text-[#D4AF37]/70 mb-2 px-3">ADMIN</p>
            <div className="space-y-0.5">
              <SidebarLink item={{ label: 'Metrics', icon: BarChart3, path: '/admin/metrics' }} active={location.pathname === '/admin/metrics'} onClick={() => go('/admin/metrics')} />
              <SidebarLink item={{ label: 'Users', icon: User, path: '/admin/users' }} active={location.pathname === '/admin/users'} onClick={() => go('/admin/users')} />
              <SidebarLink item={{ label: 'Link Health', icon: Settings, path: '/admin/link-health' }} active={location.pathname === '/admin/link-health'} onClick={() => go('/admin/link-health')} />
              <SidebarLink item={{ label: 'Curriculum Audit', icon: ShieldCheck, path: '/admin/curriculum-audit' }} active={location.pathname === '/admin/curriculum-audit'} onClick={() => go('/admin/curriculum-audit')} />
            </div>
          </div>
        )}
      </nav>

      {/* User area */}
      <div className="border-t border-white/10 pt-4 mt-4">
        {user && (
          <div className="flex items-center gap-3 px-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#7A93AC] flex items-center justify-center text-[#14213D] text-xs font-semibold flex-shrink-0">
              {(user.name || user.email).slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate text-[#FDFBF7]">{user.name || user.email.split('@')[0]}</p>
              <p className="text-[11px] text-[#FDFBF7]/40 truncate">{user.email}</p>
            </div>
          </div>
        )}
        <button
          onClick={() => navigate('/settings')}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs text-[#FDFBF7]/70 hover:text-[#FDFBF7] hover:bg-white/5 transition-colors"
          data-testid="sidebar-settings"
        >
          <Settings className="h-3.5 w-3.5" /> Preferences
        </button>
        <button
          onClick={() => { logout(); navigate('/'); }}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-xs text-[#FDFBF7]/60 hover:text-[#FDFBF7] hover:bg-white/5 transition-colors"
          data-testid="sidebar-logout"
        >
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
        <p className="text-[9px] italic text-[#FDFBF7]/30 px-3 pt-3 leading-tight">Made by Mr. Chase A. Casteel</p>
      </div>
    </aside>
  );
};

const TopBar = ({ onMenuClick, search, setSearch }) => {
  const inputRef = useRef(null);
  // Cmd/Ctrl-K to focus search
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <header
      className="sticky top-0 z-30 h-16 bg-[#FDFBF7]/90 backdrop-blur-md border-b border-[#EBE7E0] flex items-center px-4 sm:px-6 gap-4"
      data-testid="app-topbar"
    >
      <button onClick={onMenuClick} className="md:hidden text-[#14213D]" aria-label="Open menu" data-testid="topbar-menu-btn">
        <Menu className="h-5 w-5" />
      </button>

      <div className="flex-1 max-w-2xl relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6577]" />
        <input
          ref={inputRef}
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search lessons, eras, standards, people, primary sources, or classroom resources…"
          className="w-full bg-[#F9F8F4] border border-[#EBE7E0] rounded-full py-2.5 pl-11 pr-16 text-sm text-[#2D3748] placeholder:text-[#5A6577]/80 focus:outline-none focus:ring-2 focus:ring-[#14213D]/30 focus:border-[#14213D]/30 transition-all"
          data-testid="topbar-search-input"
        />
        <kbd className="hidden sm:inline-flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 px-1.5 py-0.5 bg-white border border-[#EBE7E0] rounded text-[11px] text-[#5A6577] font-mono">⌘K</kbd>
      </div>

      <div className="hidden sm:flex items-center gap-2 ml-auto">
        <button
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#14213D] hover:bg-[#F9F8F4] rounded-md transition-colors"
          data-testid="topbar-ai-shortcut"
        >
          <Sparkles className="h-3.5 w-3.5 text-[#D4AF37]" />
          <span>AI Tools</span>
        </button>
      </div>
    </header>
  );
};

export default function AppShell({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [search, setSearch] = useState('');
  const navigate = useNavigate();

  // When user submits search (Enter), route to search page with query
  useEffect(() => {
    const onKey = (e) => {
      const active = document.activeElement;
      const isSearchActive = active?.getAttribute?.('data-testid') === 'topbar-search-input';
      if (e.key === 'Enter' && isSearchActive && search.trim().length >= 2) {
        navigate(`/search?q=${encodeURIComponent(search.trim())}`);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [search, navigate]);

  return (
    <div className="flex min-h-screen bg-[#FDFBF7] print:bg-white print:min-h-0">
      {/* Desktop sidebar */}
      <div className="hidden md:block flex-shrink-0 print:hidden">
        <Sidebar />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex print:hidden" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10">
            <Sidebar onNavigate={() => setMobileOpen(false)} onClose={() => setMobileOpen(false)} />
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col min-w-0">
        <div className="print:hidden">
          <TopBar onMenuClick={() => setMobileOpen(true)} search={search} setSearch={setSearch} />
        </div>
        <main className="flex-1 overflow-y-auto print:overflow-visible">
          {children}
        </main>
      </div>
    </div>
  );
}
